import { useEffect, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import axios from 'axios';

import {
  Box,
  Flex,
  Skeleton,
  Stack,
  FormControl,
  FormLabel,
  Switch,
} from '@chakra-ui/react';

import Breadcrumbs from 'components/common/Breadcrumbs';
import CourseHeader from 'components/course/CourseHeader';
import QuestItem from 'components/quest/QuestItem';

import { fetchProgressionByCourseIdAction } from 'store/modules/progression/progression-actions';
import { progressionActions } from 'store/modules/progression/progression-slice';
import { handleErrorAndNavigate, createErrorInfo } from 'utils/errorHandler';
import { ADMIN, TRAINER } from 'utils/constants/roles';
import { subquestActions } from 'store/modules/subquest/subquest-slice';

function CoursePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { accounts } = useMsal();

  const [isCourseLoading, setIsCourseLoading] = useState(false);
  const [isQuestLoading, setIsQuestLoading] = useState(true);

  const [course, setCourse] = useState(null);
  const [quests, setQuests] = useState([]);

  // Safe access to user roles with proper null/undefined checks
  // Memoize roles to prevent unnecessary re-renders in useEffect dependencies
  const roles = useMemo(() => {
    const userAccount = accounts?.[0];
    const idTokenClaims = userAccount?.idTokenClaims;
    return idTokenClaims?.roles || [];
  }, [accounts]);

  // Check if user has admin or trainer role for conditional navigation
  const hasAdminOrTrainerRole = roles.some((role) =>
    [ADMIN, TRAINER].includes(role)
  );
  // Use appropriate courses path for edit mode navigation based on user role
  const editModeCoursePath = hasAdminOrTrainerRole
    ? `/admin/courses/${id}`
    : `/courses/${id}`;

  // Configuration for ErrorDisplay component - memoized to prevent useEffect dependency changes
  const courseEntityConfig = useMemo(
    () => ({
      name: 'Course',
      homeRoute: '/',
      courseId: parseInt(id, 10), // Add course ID for enhanced class access links
    }),
    [id]
  );

  // Clear questSubQuests state when switching to student's view
  // This ensures that stale sub-quest data is removed and only fresh data is loaded
  useEffect(() => {
    dispatch(progressionActions.resetQuestSubQuests());
    dispatch(subquestActions.enableSubQuestScoreDisplay());

    return () => {
      dispatch(subquestActions.disableSubQuestScoreDisplay());
    };
  }, [dispatch]);

  useEffect(() => {
    const fetchCourseById = async () => {
      setIsCourseLoading(true);
      try {
        const response = await axios.get(`/courses/${id}`);
        setCourse(response.data);
        document.title = `Skill Quest | ${response.data.name}`;
      } catch (error) {
        // Create standardized error info
        const errorInfo = createErrorInfo(
          error,
          'Course Not Found',
          'The course you are looking for does not exist or may have been removed.'
        );

        // Handle error and navigate using utility function
        handleErrorAndNavigate(
          dispatch,
          navigate,
          location,
          errorInfo,
          courseEntityConfig
        );
      }
      setIsCourseLoading(false);
    };
    fetchCourseById();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, dispatch, navigate, courseEntityConfig]); // location is intentionally excluded

  useEffect(() => {
    if (course) {
      const fetchCourseQuests = async () => {
        setIsQuestLoading(true);
        try {
          const response = await axios.get(`/courses/${course.id}/quests`);
          setQuests(response.data);
        } catch {
          setQuests([]);
        }
        setIsQuestLoading(false);
      };

      dispatch(fetchProgressionByCourseIdAction(course.id));

      fetchCourseQuests();
    }
  }, [course, dispatch]);

  return (
    <Flex minH="100vh" ml={{ base: 0, md: 20 }}>
      <Box flex="1" ml={2} p="5">
        <Flex direction={{ base: 'column', lg: 'row' }}>
          <Box flex="1">
            <Breadcrumbs
              mb={4}
              links={[
                { name: 'Home', path: '/' },
                { name: course?.name, path: `/courses/${id}` },
              ]}
            />
            <Flex direction={{ base: 'column', sm: 'row' }}>
              <CourseHeader
                isLoading={isCourseLoading}
                course={course}
                flex="1"
              />
              {roles?.find((role) => role === TRAINER || role === ADMIN) &&
                course?.isEditable && (
                  <FormControl
                    display="flex"
                    alignItems="start"
                    w="auto"
                    mt={{ base: 12, sm: 12, md: 0 }}
                  >
                    <FormLabel htmlFor="edit-mode-switch" mb="0" fontSize="sm">
                      Edit Mode
                    </FormLabel>
                    <Switch
                      id="edit-mode-switch"
                      isChecked={false}
                      onChange={() => navigate(editModeCoursePath)}
                    />
                  </FormControl>
                )}
            </Flex>

            <Box mt={7}>
              {isQuestLoading ? (
                <Stack>
                  <Skeleton mb={6} height="72px" borderRadius="md" />
                  <Skeleton mb={6} height="72px" borderRadius="md" />
                  <Skeleton mb={6} height="72px" borderRadius="md" />
                  <Skeleton mb={6} height="72px" borderRadius="md" />
                </Stack>
              ) : (
                <Box>
                  {quests.map((quest) => (
                    <QuestItem
                      key={quest.id}
                      courseId={parseInt(id, 10)}
                      quest={quest}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>

          {/* <Activities /> */}
        </Flex>
      </Box>
    </Flex>
  );
}

export default CoursePage;
